<script lang="ts">
  import { lunchModal } from '../../stores/lunch';
  import { getStoresContext } from '~/stores';
  import type { StoresKey } from '~/stores';
  import type { ManageAPI } from "~/manage-api/types";

  export let request_store_key: StoresKey;
  export let weekdays: string[];
  export let translations: {
    modalTitle: string;
    weekdays: string[];
  };
  export let initialDayIndex: number;
  
  // Modal styling props
  export let modalBorderRadius: string = "24px";
  export let modalBackgroundColor: string = "#FFFFFF";
  export let modalTextColor: string = "#000000";
  export let modalPaddingVertical: string = "40px";
  export let modalPaddingHorizontal: string = "40px";
  export let modalMaxWidth: string = "1200px";
  export let modalHeight: string = "600px";
  export let modalCloseButtonBorderRadius: string = "8px";
  export let modalActiveTabColor: string = "#000000";
  export let modalTabUnderlineColor: string = "#000000";
  export let modalHeaderBorderColor: string = "#EEEEEE";
  
  let _class;
  export {_class as class};
  
  $: isOpen = $lunchModal.isOpen;
  $: modalStoreKey = $lunchModal.storeKey;
  $: storeId = $lunchModal.storeId;
  
  $: ({ lunch, T } = getStoresContext(modalStoreKey || request_store_key));
  
  $: currentLunch = storeId ? $lunch.filter((l: ManageAPI.Lunch) => l.store?.id === storeId)[0] : null;
  $: t = $T;
  $: sameMenuEachDay = currentLunch?.sameMenuEachDay || false;

  $: storeName = currentLunch?.store?.name || '';
  $: generalInfo = currentLunch?.generalInformation || '';
  $: lunchTime = currentLunch?.lunchTime || '';
  $: storeIdForLogs = storeId ? String(storeId) : 'unknown';
  
  // Add default values if lunch data is missing
  $: if (!currentLunch && isOpen) {
    storeName = 'Unknown Restaurant';
    generalInfo = '';
    lunchTime = 'Lunch hours not available';
  }

  // Parse daily menus using \r\n and organize by weekday
  $: dailyMenus = currentLunch?.dailyLunchMenus?.map(menu => {
    if (!menu.lunchMenu) {
      return { day: menu.day, items: [] };
    }
    return {
      day: menu.day,
      items: menu.lunchMenu.split('\r\n').filter(item => item.trim())
    };
  }) || [];

  let selectedDayIndex = initialDayIndex;
  
  $: if (dailyMenus.length > 0 && (selectedDayIndex < 0 || selectedDayIndex >= 7)) {
    selectedDayIndex = 0;
  }

  $: selectedDayMenu = dailyMenus[selectedDayIndex] || { day: '', items: [] };
  
  function parseLunchTime(lunchTimeStr: string) {
    const times = {
      weekday: '',
      saturday: '',
      sunday: ''
    };
    
    // Prevent errors during SSR or initial page load
    if (!isOpen || !storeId) {
      return times;
    }
    
    if (!lunchTimeStr) {
      return times;
    }

    try {
      // Simple case: if it's just a time like "11-14", apply it to all days
      if (!lunchTimeStr.includes(',') && !lunchTimeStr.toLowerCase().includes('weekday') &&
          !lunchTimeStr.toLowerCase().includes('sat') && !lunchTimeStr.toLowerCase().includes('sun')) {
        times.weekday = lunchTimeStr.trim();
        times.saturday = lunchTimeStr.trim();
        times.sunday = lunchTimeStr.trim();
        return times;
      }

      const parts = lunchTimeStr.split(',').map(p => p.trim());
      parts.forEach(part => {
        if (part.toLowerCase().includes('weekday')) {
          times.weekday = part.replace(/weekdays?/i, '').trim();
        } else if (part.toLowerCase().includes('sat')) {
          times.saturday = part.replace(/sat\.?/i, '').trim();
        } else if (part.toLowerCase().includes('sun')) {
          times.sunday = part.replace(/sun\.?/i, '').trim();
        } else if (parts.length === 1) {
          // If there's only one part and it doesn't match any pattern, use it for all days
          times.weekday = part;
          times.saturday = part;
          times.sunday = part;
        }
      });

    } catch (e) {
    }

    return times;
  }

  function getLunchTimeForDay(dayIndex: number, times: any) {
    if (!isOpen || !storeId) {
      return "";
    }
    
    if (dayIndex < 0 || dayIndex > 6) {
      return "";
    }
    
    if (dayIndex === 5) return times.saturday; // Saturday
    if (dayIndex === 6) return times.sunday;   // Sunday
    return times.weekday;                      // Monday-Friday
  }

  // Check if a day has a menu
  function isDayAvailable(index: number) {
    if (!isOpen || !storeId) {
      return true;
    }
    
    if (index < 0 || index > 6) {
      return false;
    }
    
    // Weekdays (Monday-Friday) are available by default if we have any menu
    if (dailyMenus.length > 0 && index >= 0 && index <= 4) {
      return true;
    }

    return dailyMenus.some(menu => {
      try {
        if (!menu.day || !menu.day.includes('.')) {
          return false;
        }
        const [day, month, year] = menu.day.split('.').map(Number);
        if (isNaN(day) || isNaN(month) || isNaN(year) || 
            day < 1 || day > 31 || month < 1 || month > 12) {
          return false;
        }
        const menuDate = new Date(year, month - 1, day);
        const menuDayIndex = menuDate.getDay() - 1;
        return menuDayIndex === index || (index === 6 && menuDayIndex === -1); // Handle Sunday case
      } catch (e) {
        return false;
      }
    });
  }

  // Check if a day has lunch time
  function hasLunchTime(dayIndex: number, times: any) {
    if (!isOpen || !storeId) {
      return true;
    }
    
    if (dayIndex < 0 || dayIndex > 6) {
      return false;
    }
    
    // Weekdays with any lunch menu should be enabled
    if (dailyMenus.length > 0 && dayIndex >= 0 && dayIndex <= 4) {
      return true;
    }
    
    if (dayIndex === 5) {
      return !!times.saturday;
    }
    if (dayIndex === 6) {
      return !!times.sunday;
    }
    
    // Return true for weekdays to enable them as a fallback 
    // when there's any lunch menu available
    return !!times.weekday || (dailyMenus.length > 0);
  }

  // Only process these when the modal is actually open
  $: parsedLunchTimes = isOpen ? parseLunchTime(lunchTime) : { weekday: '', saturday: '', sunday: '' };
  $: selectedDayLunchTime = isOpen ? getLunchTimeForDay(selectedDayIndex, parsedLunchTimes) : '';

  let isDropdownOpen = false;
  
  function toggleDropdown() {
    isDropdownOpen = !isDropdownOpen;
  }

  function selectDay(index: number) {
    if (isDayAvailable(index) && hasLunchTime(index, parsedLunchTimes)) {
      selectedDayIndex = index;
    }
  }

  function selectOption(index: number) {
    if (isDayAvailable(index) && hasLunchTime(index, parsedLunchTimes)) {
      selectedDayIndex = index;
      isDropdownOpen = false;
    }
  }

  function closeModal() {
    lunchModal.close();
  }

  function handleKeydown(e: KeyboardEvent) {
    if (e.key === 'Escape') {
      closeModal();
    }
  }

  let modalBackdrop: HTMLElement;
  
  $: if (isOpen && modalBackdrop) {
    setTimeout(() => {
      modalBackdrop.focus();
    }, 0);
  }

  // Modal styling - create reactive style string
  $: modalStyle = `
    --lunch-modal-border-radius: ${modalBorderRadius};
    --lunch-modal-background-color: ${modalBackgroundColor};
    --lunch-modal-text-color: ${modalTextColor};
    --lunch-modal-padding-vertical: ${modalPaddingVertical};
    --lunch-modal-padding-horizontal: ${modalPaddingHorizontal};
    --lunch-modal-max-width: ${modalMaxWidth};
    --lunch-modal-height: ${modalHeight};
    --lunch-modal-close-button-border-radius: ${modalCloseButtonBorderRadius};
    --lunch-modal-active-tab-color: ${modalActiveTabColor};
    --lunch-modal-tab-underline-color: ${modalTabUnderlineColor};
    --lunch-modal-header-border-color: ${modalHeaderBorderColor};
  `;
</script>

{#if isOpen}
  <div 
    class="modal-backdrop"
    role="button"
    on:click={closeModal}
    on:keydown={handleKeydown}
    tabindex="0"
    bind:this={modalBackdrop}
  >
    <div 
      class="modal-wrapper" 
      on:click|stopPropagation
      on:keydown|stopPropagation
      role="presentation"
      style={modalStyle}
    >
      <div class="modal-content {_class}" role="dialog" aria-modal="true" aria-labelledby="modal-title">
        {#if !sameMenuEachDay}
          <div class="modal-header">
            <nav class="weekdays" aria-label="Week days">
              {#each weekdays as day, index}
                <button
                  type="button"
                  class="day-tab"
                  class:active={index === selectedDayIndex && isDayAvailable(index)}
                  class:closed={!isDayAvailable(index) || !hasLunchTime(index, parsedLunchTimes)}
                  on:click={() => isDayAvailable(index) && hasLunchTime(index, parsedLunchTimes) && selectDay(index)}
                  disabled={!isDayAvailable(index) || !hasLunchTime(index, parsedLunchTimes)}
                  aria-selected={index === selectedDayIndex}
                  role="tab"
                >
                  {translations.weekdays[index]}
                </button>
              {/each}
            </nav>
            <button type="button" class="responsive-close-button" on:click={closeModal} aria-label="Close modal">
              <span class="close-icon"></span>
            </button>
          </div>
        {/if}
        <div class="modal-body" class:no-header={sameMenuEachDay}>
          <h2 id="modal-title">
            {storeName} - {translations.modalTitle}
            {#if selectedDayLunchTime}
              <span class="lunch-hours">{selectedDayLunchTime}</span>
            {/if}
          </h2>
          {#if generalInfo}
            <p class="general-info">{generalInfo}</p>
          {/if}

          {#if !sameMenuEachDay}
            <div class="day-select">
              <button 
                type="button" 
                class="dropdown-trigger" 
                on:click={toggleDropdown}
                aria-haspopup="listbox"
                aria-expanded={isDropdownOpen}
              >
                {translations.weekdays[selectedDayIndex]}
              </button>
              
              {#if isDropdownOpen}
                <div 
                  class="dropdown-options" 
                  role="listbox"
                  aria-label="Select day"
                >
                  {#each weekdays as day, index}
                    <button
                      type="button"
                      class="dropdown-option"
                      class:disabled={!isDayAvailable(index) || !hasLunchTime(index, parsedLunchTimes)}
                      class:selected={index === selectedDayIndex}
                      on:click={() => selectOption(index)}
                      role="option"
                      aria-selected={index === selectedDayIndex}
                      disabled={!isDayAvailable(index) || !hasLunchTime(index, parsedLunchTimes)}
                    >
                      {translations.weekdays[index]}
                    </button>
                  {/each}
                </div>
              {/if}
            </div>
          {/if}

          <div class="menu-items" role="list">
            {#each selectedDayMenu.items as item}
              <div class="menu-item" role="listitem">{item}</div>
            {/each}
          </div>
        </div>
        {#if sameMenuEachDay}
          <button type="button" class="responsive-close-button" on:click={closeModal} aria-label="Close modal">
            <span class="close-icon"></span>
          </button>
        {/if}
      </div>
      <button 
        type="button" 
        class="close-button {_class}" 
        on:click={closeModal}
        aria-label="Close modal"
      >
        <span class="close-icon"></span>
      </button>
    </div>
  </div>
{/if}

<style lang="scss">
  .modal-backdrop {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
  }

  .modal-wrapper {
    position: relative;
    width: 90%;
    max-width: var(--lunch-modal-max-width, 1200px);
  }

  .modal-content {
    background: var(--lunch-modal-background-color, white) !important;
    border-radius: var(--lunch-modal-border-radius, 24px);
    padding: var(--lunch-modal-padding-vertical, 40px) var(--lunch-modal-padding-horizontal, 40px);
    padding-top: 1rem;
    height: var(--lunch-modal-height, 600px);
    display: flex;
    flex-direction: column;
    position: relative;
    color: var(--lunch-modal-text-color, #000);

    .modal-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      border-bottom: 1px solid var(--lunch-modal-header-border-color, #EEEEEE);
      padding: 0;
      margin-bottom: 24px;

      @media screen and (max-width: 991px) {
        border-bottom: none;
        margin-bottom: 0;
      }

      .weekdays {
        display: flex;
        gap: 0;

        @media screen and (max-width: 991px) {
            display: none;
        }

        .day-tab {
          border: none;
          background: none;
          appearance: none;
          -webkit-appearance: none;
          -webkit-tap-highlight-color: rgba(0,0,0,0);
          padding: 8px 16px;
          font-size: 14px;
          font-weight: 400;
          line-height: 20px;
          text-transform: uppercase;
          cursor: pointer;
          color: var(--lunch-modal-text-color, #000);
          text-decoration: none;

          &.active {
            font-weight: 700;
            color: var(--lunch-modal-active-tab-color, #000);
            text-decoration-line: underline;
            text-decoration-color: var(--lunch-modal-tab-underline-color, #000);
          }

          &.closed {
            cursor: not-allowed;
            text-decoration: line-through;
          }
        }
      }
    }

    .modal-body {
      flex: 1;
      overflow-y: auto;
      padding: 0;
      border: none;
      color: var(--lunch-modal-text-color, #000);

      &.no-header {
        padding-top: 20px;
        padding-right: 60px; // Extra padding to accommodate the close button
        
        @media screen and (max-width: 991px) {
          padding-right: 40px;
        }
      }

      h2 {
        font-size: 38px;
        font-weight: 600;
        line-height: 46px;
        letter-spacing: 0.02em;
        text-align: left;
        text-transform: uppercase;
        color: var(--lunch-modal-text-color, #000);
      }

      .general-info {
        font-size: 16px;
        font-weight: 400;
        line-height: 24px;
        margin-bottom: 16px;
        color: var(--lunch-modal-text-color, #000);
      }

      .day-select {
        display: none;
        margin-bottom: 24px;
        position: relative;

        @media screen and (max-width: 991px) {
          display: block;
        }

        .dropdown-trigger {
          width: 100%;
          -webkit-appearance: none;
          -webkit-tap-highlight-color: rgba(0,0,0,0);
          background: var(--lunch-modal-background-color, white) url("data:image/svg+xml,%3Csvg width='14' height='8' viewBox='0 0 14 8' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1L7 7L13 1' stroke='black' stroke-width='1.5'/%3E%3C/svg%3E") no-repeat;
          background-position: right 12px center;
          border: 1px solid #D3D3D3;
          border-radius: 10px;
          padding: 8px 12px;
          cursor: pointer;
          text-align: left;
          font-size: 16px;
          font-weight: 600;
          line-height: 24px;
          letter-spacing: 1.5px;
          text-transform: uppercase;
          color: var(--lunch-modal-text-color, #000);
          text-decoration: none;

          &:focus {
            outline: none;
            border-color: var(--lunch-modal-active-tab-color, #000);
          }
        }

        .dropdown-options {
          position: absolute;
          top: calc(100% + 8px);
          left: 0;
          width: 100%;
          background: var(--lunch-modal-background-color, white);
          border: 1px solid #D3D3D3;
          border-radius: 10px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
          z-index: 1000;
          -webkit-overflow-scrolling: touch;

          .dropdown-option {
            width: 100%;
            -webkit-appearance: none;
            -webkit-tap-highlight-color: rgba(0,0,0,0);
            padding: 8px 12px;
            border: none;
            background: none;
            text-align: left;
            cursor: pointer;
            font-size: 16px;
            font-weight: 600;
            line-height: 24px;
            letter-spacing: 1.5px;
            text-transform: uppercase;
            color: var(--lunch-modal-text-color, #000);
            text-decoration: none;
            user-select: none;

            &:hover:not(.disabled) {
              background-color: #F5F5F5;
            }

            &:active:not(.disabled) {
              background-color: #EAEAEA;
            }

            &.selected {
              font-weight: 700;
              color: var(--lunch-modal-active-tab-color, #000);
            }

            &.disabled {
              color: #D3D3D3;
              cursor: not-allowed;
            }

            &:first-child {
              border-radius: 10px 10px 0 0;
            }

            &:last-child {
              border-radius: 0 0 10px 10px;
            }
          }
        }
      }

      .menu-items {
        display: flex;
        flex-direction: column;
        gap: 16px;
        padding-bottom: 24px;

        .menu-item {
          font-size: 18px;
          font-weight: 400;
          line-height: 28px;
          color: var(--lunch-modal-text-color, #000);
        }
      }
    }
  }

  .close-button {
    position: absolute;
    top: 0;
    right: -60px;
    background-color: var(--lunch-modal-background-color, #ffffff) !important;
    border: none;
    border-radius: var(--lunch-modal-close-button-border-radius, 8px);
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    padding: 0;
    -webkit-appearance: none;
    -webkit-tap-highlight-color: rgba(0,0,0,0);
    box-shadow: none;
    outline: none;

    &:hover {
      background-color: #f5f5f5 !important;
      
      .close-icon::before,
      .close-icon::after {
        background-color: var(--lunch-modal-text-color, #333);
      }
    }

    @media screen and (min-width: 992px) and (max-width: 1200px) {
        display: none;
    }

    @media screen and (max-width: 991px) {
        display: flex;
        position: absolute;
        top: 16px;
        right: 16px;
        z-index: 10;
    }
    
    @media screen and (max-width: 768px) {
        position: absolute;
        top: 16px;
        right: 16px;
        z-index: 10;
    }
  }
  
  .close-icon {
    position: relative;
    width: 16px;
    height: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    
    &::before,
    &::after {
      content: '';
      position: absolute;
      top: 50%;
      left: 0;
      width: 100%;
      height: 2px;
      background-color: var(--lunch-modal-text-color, #000000);
      transition: background-color 0.2s;
    }
    
    &::before {
      transform: rotate(45deg);
    }
    
    &::after {
      transform: rotate(-45deg);
    }
  }

  .responsive-close-button {
    display: none;

    @media screen and (min-width: 992px) and (max-width: 1200px) {
        background-color: var(--lunch-modal-background-color, #ffffff) !important;
        border: none;
        border-radius: var(--lunch-modal-close-button-border-radius, 8px);
        width: 40px;
        height: 40px;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        color: var(--lunch-modal-text-color, #000);
        padding: 0;
        -webkit-appearance: none;
        -webkit-tap-highlight-color: rgba(0,0,0,0);
        position: absolute;
        top: 16px;
        right: 16px;
        z-index: 10;
        box-shadow: none;
        outline: none;

        &:hover {
          background-color: #f5f5f5 !important;
          
          .close-icon::before,
          .close-icon::after {
            background-color: var(--lunch-modal-text-color, #333);
          }
        }
    }
  }
</style> 